// Analytics / engagement helpers — all Firestore-backed.
// Collections used: product_stats, search_logs, favorites, notifications,
// notification_reads, coupons, carts, users.
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit as fsLimit, serverTimestamp, increment, onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ---------- product stats (views / favorite counts) ----------
export async function trackProductView(productId) {
  if (!productId) return;
  const ref = doc(db, "product_stats", productId);
  try {
    await setDoc(ref, { views: increment(1), updated_at: serverTimestamp() }, { merge: true });
  } catch (_e) { /* guests without write permission on product_stats — ignore */ }
}

export async function listTopProducts(field, lim = 10) {
  try {
    const snap = await getDocs(query(collection(db, "product_stats"), orderBy(field, "desc"), fsLimit(lim)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (_e) { return []; }
}

// ---------- search logging ----------
export async function logSearch(qstr, userId) {
  const term = (qstr || "").trim().toLowerCase();
  if (!term) return;
  try {
    await addDoc(collection(db, "search_logs"), {
      query: term, user_id: userId || null, ts: serverTimestamp(),
    });
  } catch (_e) { /* ignore */ }
}

export async function listTopSearches(lim = 10) {
  try {
    const snap = await getDocs(query(collection(db, "search_logs"), orderBy("ts", "desc"), fsLimit(500)));
    const counts = {};
    snap.docs.forEach((d) => {
      const q = d.data().query || "";
      counts[q] = (counts[q] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([q, count]) => ({ query: q, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, lim);
  } catch (_e) { return []; }
}

// ---------- favorites (wishlist) ----------
function favId(userId, productId) { return `${userId}__${productId}`; }

export async function toggleFavorite(userId, productId) {
  if (!userId || !productId) return false;
  try {
    const ref = doc(db, "favorites", favId(userId, productId));
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await deleteDoc(ref);
      try { await setDoc(doc(db, "product_stats", productId), { favorites: increment(-1) }, { merge: true }); } catch (_e) { /* ignore */ }
      return false;
    }
    await setDoc(ref, { user_id: userId, product_id: productId, created_at: serverTimestamp() });
    try { await setDoc(doc(db, "product_stats", productId), { favorites: increment(1) }, { merge: true }); } catch (_e) { /* ignore */ }
    return true;
  } catch (_e) { return false; }
}

export async function getUserFavoriteIds(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(query(collection(db, "favorites"), where("user_id", "==", userId)));
    return snap.docs.map((d) => d.data().product_id);
  } catch (_e) { return []; }
}

export function subscribeUserFavoriteIds(userId, cb) {
  if (!userId) { cb([]); return () => {}; }
  return onSnapshot(
    query(collection(db, "favorites"), where("user_id", "==", userId)),
    (snap) => cb(snap.docs.map((d) => d.data().product_id)),
    () => cb([]),
  );
}

// ---------- notifications ----------
export async function createNotification({ title, body, target_user_id = null, type = "info" }) {
  return await addDoc(collection(db, "notifications"), {
    title, body, target_user_id, type, created_at: serverTimestamp(),
  });
}

export async function listNotifications(lim = 50) {
  try {
    const snap = await getDocs(query(collection(db, "notifications"), orderBy("created_at", "desc"), fsLimit(lim)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (_e) { return []; }
}

export async function listNotificationsForUser(userId, lim = 30) {
  try {
    const all = await listNotifications(lim);
    return all.filter((n) => !n.target_user_id || n.target_user_id === userId);
  } catch (_e) { return []; }
}

export async function deleteNotification(id) {
  await deleteDoc(doc(db, "notifications", id));
}

export async function markNotificationRead(userId, nId) {
  if (!userId || !nId) return;
  try {
    await setDoc(doc(db, "notification_reads", `${userId}__${nId}`), {
      user_id: userId, notification_id: nId, read_at: serverTimestamp(),
    });
  } catch (_e) { /* ignore */ }
}

export async function getReadNotificationIds(userId) {
  if (!userId) return new Set();
  try {
    const snap = await getDocs(query(collection(db, "notification_reads"), where("user_id", "==", userId)));
    return new Set(snap.docs.map((d) => d.data().notification_id));
  } catch (_e) { return new Set(); }
}

// ---------- coupons ----------
export async function listCoupons() {
  const snap = await getDocs(query(collection(db, "coupons"), orderBy("created_at", "desc")));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => !c.kind || c.kind === "coupon"); // hide gift-card kinds from regular coupon list
}

export async function createCoupon(data) {
  return await addDoc(collection(db, "coupons"), {
    kind: "coupon",
    code: (data.code || "").toUpperCase().trim(),
    discount_percent: Number(data.discount_percent) || 0,
    discount_amount: Number(data.discount_amount) || 0,
    user_id: data.user_id || null,       // null = sitewide
    max_uses: Number(data.max_uses) || 0, // 0 = unlimited
    used: 0,
    active: data.active !== false,
    expires_at: data.expires_at || null,
    created_at: serverTimestamp(),
  });
}

export async function updateCoupon(id, patch) {
  await updateDoc(doc(db, "coupons", id), patch);
}

export async function deleteCoupon(id) {
  await deleteDoc(doc(db, "coupons", id));
}

export async function validateCoupon(code, userId) {
  const norm = (code || "").toUpperCase().trim();
  if (!norm) return null;
  const snap = await getDocs(query(collection(db, "coupons"), where("code", "==", norm), fsLimit(1)));
  if (snap.empty) return null;
  const c = { id: snap.docs[0].id, ...snap.docs[0].data() };
  // Skip gift card entries — they go through validateGiftCardCode.
  if (c.kind === "gift_card" || c.kind === "gift_template") return null;
  if (c.active === false) return null;
  if (c.user_id && c.user_id !== userId) return null;
  if (c.max_uses > 0 && c.used >= c.max_uses) return null;
  if (c.expires_at) {
    const exp = c.expires_at?.toDate ? c.expires_at.toDate() : new Date(c.expires_at);
    if (exp && exp < new Date()) return null;
  }
  return c;
}

export async function incrementCouponUse(id) {
  try {
    await updateDoc(doc(db, "coupons", id), { used: increment(1) });
  } catch (_e) { /* ignore */ }
}

// ---------- carts (server-side mirror for live viewing) ----------
export async function saveCartSnapshot(sessionId, { userId, userEmail, items }) {
  if (!sessionId) return;
  try {
    await setDoc(doc(db, "carts", sessionId), {
      session_id: sessionId,
      user_id: userId || null,
      user_email: userEmail || null,
      items: items || [],
      count: (items || []).reduce((s, i) => s + (i.qty || 0), 0),
      total_azn: (items || []).reduce((s, i) => s + (i.price_azn || 0) * (i.qty || 0), 0),
      updated_at: serverTimestamp(),
    }, { merge: true });
  } catch (_e) { /* ignore — guest without write perm */ }
}

export async function listLiveCarts() {
  try {
    const snap = await getDocs(query(collection(db, "carts"), orderBy("updated_at", "desc"), fsLimit(50)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => (c.items || []).length > 0);
  } catch (_e) { return []; }
}

// ---------- users ----------
export async function listCustomers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
}

export async function listOrdersByUser(userId) {
  try {
    const snap = await getDocs(query(collection(db, "orders"), where("user_id", "==", userId)));
    // Yalnız ödənilmiş sifarişləri qaytarırıq (ödənilməmiş = awaiting_payment / payment_failed)
    const HIDDEN = new Set(["awaiting_payment", "payment_failed"]);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((o) => !HIDDEN.has(o.status));
  } catch (_e) { return []; }
}
